import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Injectable,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';


@Injectable({
  providedIn: 'root'
})
export class AvanoRadioControlRegistry {
  private accessors: any[] = [];

  add(control: NgControl, accessor: AvanoRadiobuttonComponent) {
    this.accessors.push([control, accessor]);
  }

  remove(accessor: AvanoRadiobuttonComponent) {
    this.accessors = this.accessors.filter((c) => {
      return c[1] !== accessor;
    });
  }

  select(accessor: AvanoRadiobuttonComponent) {
    this.accessors.forEach((c) => {
      if (this.isSameGroup(c, accessor) && c[1] !== accessor) {
        c[1].writeValue(accessor.value);
      }
    });
  }

  private isSameGroup(controlPair: [NgControl, AvanoRadiobuttonComponent], accessor: AvanoRadiobuttonComponent): boolean {
    if (!controlPair[0].control) {
      return false;
    }

    return controlPair[0].control.root === accessor.control.control.root && controlPair[1].name === accessor.name;
  }
}

export const RADIO_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AvanoRadiobuttonComponent),
  multi: true
};

@Component({
  selector: 'avano-radiobutton',
  templateUrl: './avano-radiobutton.component.html',
  styleUrls: ['./avano-radiobutton.component.scss'],
  providers: [RADIO_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvanoRadiobuttonComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() value: unknown;

  @Input() formControlName: string;

  @Input() name: string;

  @Input() disabled: boolean;

  @Input() readonly: boolean;

  @ViewChild('rb') inputViewChild: ElementRef;

  checked: boolean;

  focused: boolean;

  control: NgControl;

  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/ban-types
  public onModelChange: Function = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/ban-types
  public onModelTouched: Function = () => {};

  constructor(public cd: ChangeDetectorRef, private injector: Injector, private registry: AvanoRadioControlRegistry) {}

  ngOnInit() {
    // Cannot use @Optional-inject in the constructor -> gives circular dependency?
    this.control = this.injector.get(NgControl);
    this.checkName();
    this.registry.add(this.control, this);
  }

  handleClick(event, radioButton, focus) {
    event.preventDefault();

    if (this.disabled) {
      return;
    }

    this.select();

    if (focus) {
      radioButton.focus();
    }
  }

  select() {
    if (!this.disabled) {
      this.inputViewChild.nativeElement.checked = true;
      this.checked = true;
      this.onModelChange(this.value);
      this.registry.select(this);
    }
  }

  writeValue(value: unknown): void {
    this.checked = value == this.value;

    if (this.inputViewChild && this.inputViewChild.nativeElement) {
      this.inputViewChild.nativeElement.checked = this.checked;
    }

    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
    this.cd.markForCheck();
  }

  onInputFocus() {
    this.focused = true;
  }

  onInputBlur() {
    this.focused = false;
    this.onModelTouched();
  }

  onChange() {
    this.select();
  }

  focus() {
    this.inputViewChild.nativeElement.focus();
  }

  ngOnDestroy() {
    this.registry.remove(this);
  }

  private checkName() {
    if (this.name && this.formControlName && this.name !== this.formControlName) {
      this.throwNameError();
    }
    if (!this.name && this.formControlName) {
      this.name = this.formControlName;
    }
  }

  private throwNameError() {
    throw new Error(`
          If you define both a name and a formControlName attribute on your radio button, their values
          must match. Ex: <avano-radiobutton formControlName="food" name="food"></avano-radiobutton>
        `);
  }
}
